import { configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

import { api } from './api';
import {
  MOMENTS_BOUNDARY_STORAGE_KEY,
  momentsPrivateMiddleware,
} from './momentsPrivateMiddleware';
import appReducer, {
  clearMomentsPrivateStateFromOtherTab,
} from './slices/appSlice';
import mfaReducer from './slices/mfaSlice';

import { schedulingAssistantApi } from '@/features/schedulingAssistant/api';

// MEN-4239: the app runs on the `store` singleton below, but Storybook needs a
// fresh store per story so one story's RTK Query cache cannot leak into the
// next and make a visual-audit screenshot depend on render order.
export const makeStore = () => {
  const appStore = configureStore({
    devTools: process.env.NODE_ENV !== 'production',
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        momentsPrivateMiddleware,
        api.middleware,
        schedulingAssistantApi.middleware,
      ),
    // eslint-disable-next-line sort-keys -- Preserve middleware dispatch inference before configuring enhancers.
    enhancers: (getDefaultEnhancers) =>
      getDefaultEnhancers({ autoBatch: { type: 'tick' } }),
    reducer: {
      api: api.reducer,
      app: appReducer,
      mfa: mfaReducer,
      [schedulingAssistantApi.reducerPath]: schedulingAssistantApi.reducer,
    },
  });
  // Custom handlers avoid RTK Query's singleton default listener binding so
  // every Storybook/test store has its own explicit, disposable listeners.
  const dispose = setupListeners(appStore.dispatch, (dispatch, actions) => {
    if (typeof window === 'undefined') {
      return () => undefined;
    }
    const focus = () => dispatch(actions.onFocus());
    const visibility = () =>
      dispatch(
        document.visibilityState === 'visible'
          ? actions.onFocus()
          : actions.onFocusLost(),
      );
    const online = () => dispatch(actions.onOnline());
    const offline = () => dispatch(actions.onOffline());
    const boundary = (event: StorageEvent) => {
      if (event.key === MOMENTS_BOUNDARY_STORAGE_KEY && event.newValue) {
        dispatch(clearMomentsPrivateStateFromOtherTab());
      }
    };
    window.addEventListener('focus', focus);
    window.addEventListener('visibilitychange', visibility);
    window.addEventListener('online', online);
    window.addEventListener('offline', offline);
    window.addEventListener('storage', boundary);
    return () => {
      window.removeEventListener('focus', focus);
      window.removeEventListener('visibilitychange', visibility);
      window.removeEventListener('online', online);
      window.removeEventListener('offline', offline);
      window.removeEventListener('storage', boundary);
    };
  });
  return Object.assign(appStore, { dispose });
};

export const store = makeStore();

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
